import React, { useState, ComponentType } from 'react';
import { ChevronLeft, ChevronRight, X, Download, Play, Image as ImageIcon, FolderOpen, CheckCircle2 } from 'lucide-react';
import { POSTED_PLATFORMS, PostedPlatform } from '@crewcast/shared';
import PageLoader from '../components/PageLoader';
import LoadMoreButton from '../components/LoadMoreButton';
import { FacebookIcon, InstagramIcon, TikTokIcon } from '../components/icons/BrandIcons';
import { authConfig } from '../config/auth';
import { useAuth } from '../contexts/AuthContext';
import { usePaginatedFetch } from '../hooks/usePaginatedFetch';
import { linkify } from '../utils/linkify';
import '../styles/content-vault.css';

// How the "Posted to" platform toggles render:
//   'text'  — platform names only. Safe to ship today.
//   'icons' — official logos only (no names beside them), sized and spaced per
//             the Meta brand guidelines: Instagram glyph ≥29px with half-glyph
//             clear space, Facebook logo ≥16px and never next to the word
//             "Facebook". A row of icons needs no call to action.
// 'icons' uses the official TikTok logo, which needs TikTok's written permission,
// so keep this on 'text' for deploys until the TikTok developer app is approved.
// Mirrored in packages/mobile/screens/ContentVaultScreen.tsx.
const PLATFORM_CHIP_MODE: 'text' | 'icons' = 'text';

// Display metadata for the postable platforms. Order follows POSTED_PLATFORMS.
const PLATFORM_META: Record<PostedPlatform, { label: string; icon: ComponentType<{ size?: number | string }> }> = {
  instagram: { label: 'Instagram', icon: InstagramIcon },
  tiktok: { label: 'TikTok', icon: TikTokIcon },
  facebook: { label: 'Facebook', icon: FacebookIcon },
};

interface ContentItem {
  submissionId: string;
  challengeId: string;
  challengeTitle: string;
  challengeDescription?: string | null;
  userId: string;
  employeeName: string;
  contributors: string[];
  mediaUrls: string[];
  submittedAt: string;
  reviewedAt: string;
  zipKey?: string | null;
  postedTo?: PostedPlatform[];
  postedAt?: string | null;
}

export default function ContentVaultPage() {
  const { token } = useAuth();
  const [selectedContent, setSelectedContent] = useState<ContentItem | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  // Local optimistic overrides for postedTo, keyed by submissionId. Avoids refetching
  // (which would reset pagination) while still reflecting selections instantly.
  const [postedOverrides, setPostedOverrides] = useState<Record<string, PostedPlatform[]>>({});
  const [postingIds, setPostingIds] = useState<Record<string, boolean>>({});

  const {
    items: content,
    loading,
    loadingMore,
    hasMore,
    error,
    loadMore,
  } = usePaginatedFetch<ContentItem>({
    url: `${authConfig.apiUrl}/api/admin/content-vault`,
    token,
    dataKey: 'submissions',
    limit: 20,
  });

  const isVideo = (url: string) => {
    if (!url) return false;
    const videoExtensions = ['.mp4', '.mov', '.avi', '.webm', '.m4v'];
    return videoExtensions.some(ext => url.toLowerCase().includes(ext));
  };

  const getFileExtension = (url: string) => {
    if (!url) return 'mp4';
    const match = url.match(/\.(mp4|mov|avi|webm|m4v|jpg|jpeg|png|gif)(\?|$)/i);
    return match ? match[1].toLowerCase() : 'mp4';
  };

  const handleDownload = async (item: ContentItem, url: string, assetIndex: number, totalAssets: number) => {
    try {
      if (!token) throw new Error('Not authenticated');
      if (!url) throw new Error('Media URL is missing from item');

      const momentName = item.challengeTitle.replace(/[^a-zA-Z0-9]/g, '_');
      const employeeName = item.employeeName.replace(/[^a-zA-Z0-9]/g, '_');
      const fileExtension = getFileExtension(url);
      const assetSuffix = totalAssets > 1 ? `_${assetIndex + 1}` : '';
      const fileName = `${momentName}_${employeeName}${assetSuffix}.${fileExtension}`;

      const response = await fetch(`${authConfig.apiUrl}/api/admin/content-vault/download-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ mediaUrl: url, fileName }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || 'Failed to generate download URL');
      }

      const data = await response.json();
      const a = document.createElement('a');
      a.href = data.downloadUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      console.error('Download failed:', err);
      alert('Failed to download media');
    }
  };

  const handleDownloadZip = async (item: ContentItem) => {
    try {
      if (!token) throw new Error('Not authenticated');

      const momentName = item.challengeTitle.replace(/[^a-zA-Z0-9]/g, '_');
      const employeeName = item.employeeName.replace(/[^a-zA-Z0-9]/g, '_');
      const fileName = `${momentName}_${employeeName}_all_assets.zip`;

      const response = await fetch(`${authConfig.apiUrl}/api/admin/content-vault/submission-zip-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ submissionId: item.submissionId, fileName }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || 'Failed to generate zip URL');
      }

      const data = await response.json();
      const a = document.createElement('a');
      a.href = data.downloadUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      console.error('Zip download failed:', err);
      alert('Failed to download zip. Please download files individually.');
    }
  };

  // Effective postedTo = optimistic override if present, else the server value.
  const getPostedTo = (item: ContentItem): PostedPlatform[] =>
    postedOverrides[item.submissionId] ?? item.postedTo ?? [];

  // Toggle a single platform on/off and persist the full resulting list.
  const togglePlatform = async (item: ContentItem, platform: PostedPlatform) => {
    if (!token || postingIds[item.submissionId]) return;

    const current = getPostedTo(item);
    const next = current.includes(platform)
      ? current.filter((p) => p !== platform)
      : POSTED_PLATFORMS.filter((p) => p === platform || current.includes(p)); // keep canonical order

    // Optimistic update
    setPostedOverrides((prev) => ({ ...prev, [item.submissionId]: next }));
    setPostingIds((prev) => ({ ...prev, [item.submissionId]: true }));

    try {
      const response = await fetch(
        `${authConfig.apiUrl}/api/admin/content-vault/${item.submissionId}/posted`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ postedTo: next }),
        }
      );
      if (!response.ok) throw new Error('Failed to update posted platforms');
    } catch (err) {
      console.error('Toggle platform failed:', err);
      // Roll back on failure
      setPostedOverrides((prev) => ({ ...prev, [item.submissionId]: current }));
      alert('Failed to update posted platforms');
    } finally {
      setPostingIds((prev) => {
        const copy = { ...prev };
        delete copy[item.submissionId];
        return copy;
      });
    }
  };

  const openModal = (item: ContentItem) => {
    setSelectedContent(item);
    setLightboxIndex(0);
  };

  const closeModal = () => {
    setSelectedContent(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="content-vault-container">
        <PageLoader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="content-vault-container">
        <div className="vault-error">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="content-vault-container">
      <div className="vault-header">
        <div>
          <h1 className="vault-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <FolderOpen size={28} style={{ color: 'var(--color-primary)', flexShrink: 0 }} />
            Content Vault
          </h1>
          <p className="vault-subtitle">Brand-owned assets ready for marketing</p>
        </div>
      </div>

      {content.length === 0 ? (
        <div className="vault-empty">
          <p>No approved content yet.</p>
          <p className="empty-subtitle">Approved submissions will appear here.</p>
        </div>
      ) : (
        <div className="vault-grid">
          {content.map((item) => (
            <div key={item.submissionId} className="vault-card" onClick={() => openModal(item)}>
              <div className="vault-thumbnail">
                {isVideo(item.mediaUrls[0]) ? (
                  <video src={`${item.mediaUrls[0]}#t=0.001`} className="thumbnail-video" muted playsInline preload="metadata" />
                ) : (
                  <img src={item.mediaUrls[0]} alt={item.challengeTitle} className="thumbnail-image" />
                )}
                <div className="play-overlay">
                  <div className="play-icon">{isVideo(item.mediaUrls[0]) ? <Play size={24} color="#1f2937" style={{ marginLeft: '2px' }} /> : <ImageIcon size={24} color="#1f2937" />}</div>
                </div>
                {item.mediaUrls.length > 1 && (
                  <span className="vault-asset-count-badge">+{item.mediaUrls.length - 1} more</span>
                )}
              </div>
              <div className="vault-card-footer">
                <div className="card-meta">
                  <div className="card-meta-label">Moment</div>
                  <div className="card-moment">{item.challengeTitle}</div>
                  <div className="card-meta-label" style={{ marginTop: '8px' }}>
                    {item.contributors && item.contributors.length > 1 ? 'Contributors' : 'Creator'}
                  </div>
                  <div className="card-employee">
                    {item.contributors && item.contributors.length > 0
                      ? item.contributors.join(', ')
                      : item.employeeName}
                  </div>
                  <div className="card-date">{formatDate(item.reviewedAt)}</div>
                  {getPostedTo(item).length > 0 ? (
                    <div className="vault-posted-line">
                      <CheckCircle2 size={12} />
                      Posted &middot; {getPostedTo(item).map((p) => PLATFORM_META[p].label).join(', ')}
                    </div>
                  ) : (
                    <div className="vault-posted-line vault-posted-line--off">Not posted yet</div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <LoadMoreButton onClick={loadMore} loading={loadingMore} hasMore={hasMore} />

      {/* Media Modal */}
      {selectedContent && (
        <div className="vault-modal" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>
              <X size={18} />
            </button>

            <div className="modal-video-container" style={{ position: 'relative' }}>
              {isVideo(selectedContent.mediaUrls[lightboxIndex]) ? (
                <video
                  key={lightboxIndex}
                  src={selectedContent.mediaUrls[lightboxIndex]}
                  controls
                  autoPlay
                  muted
                  playsInline
                  className="modal-video"
                />
              ) : (
                <img
                  src={selectedContent.mediaUrls[lightboxIndex]}
                  alt={selectedContent.challengeTitle}
                  className="modal-image"
                />
              )}
              {selectedContent.mediaUrls.length > 1 && (
                <>
                  <button
                    className="vault-modal-nav vault-modal-nav--prev"
                    onClick={() => setLightboxIndex((i) => i - 1)}
                    disabled={lightboxIndex === 0}
                  >
                    <ChevronLeft size={24} />
                  </button>
                  <button
                    className="vault-modal-nav vault-modal-nav--next"
                    onClick={() => setLightboxIndex((i) => i + 1)}
                    disabled={lightboxIndex === selectedContent.mediaUrls.length - 1}
                  >
                    <ChevronRight size={24} />
                  </button>
                </>
              )}
            </div>

            <div className="modal-footer">
              <div className="modal-meta">
                <h3 className="modal-moment">{selectedContent.challengeTitle}</h3>
                {selectedContent.challengeDescription && (
                  <p className="modal-challenge-description">{linkify(selectedContent.challengeDescription)}</p>
                )}
                <p className="modal-meta-label">
                  {selectedContent.contributors && selectedContent.contributors.length > 1 ? 'Contributors' : 'Creator'}
                </p>
                <p className="modal-employee">
                  {selectedContent.contributors && selectedContent.contributors.length > 0
                    ? selectedContent.contributors.join(', ')
                    : selectedContent.employeeName}
                </p>
                <p className="modal-date">{formatDate(selectedContent.reviewedAt)}</p>
              </div>

              {selectedContent.mediaUrls.length > 1 && (
                <div className="vault-filmstrip">
                  {selectedContent.mediaUrls.map((url, i) => (
                    <div
                      key={i}
                      className={`vault-filmstrip-thumb${i === lightboxIndex ? ' vault-filmstrip-thumb--active' : ''}`}
                      onClick={() => setLightboxIndex(i)}
                    >
                      {isVideo(url) ? (
                        <video src={`${url}#t=0.001`} className="vault-filmstrip-media" muted playsInline preload="metadata" />
                      ) : (
                        <img src={url} alt="" className="vault-filmstrip-media" />
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="vault-posted-selector">
                <p className="modal-meta-label">Posted to</p>
                <div className={`vault-platform-chips${PLATFORM_CHIP_MODE === 'icons' ? ' vault-platform-chips--icons' : ''}`}>
                  {POSTED_PLATFORMS.map((platform) => {
                    const active = getPostedTo(selectedContent).includes(platform);
                    const { label, icon: Icon } = PLATFORM_META[platform];
                    return (
                      <button
                        key={platform}
                        className={`vault-platform-chip${PLATFORM_CHIP_MODE === 'icons' ? ' vault-platform-chip--icon' : ''}${active ? ' vault-platform-chip--active' : ''}`}
                        onClick={() => togglePlatform(selectedContent, platform)}
                        disabled={postingIds[selectedContent.submissionId]}
                        aria-pressed={active}
                        aria-label={PLATFORM_CHIP_MODE === 'icons' ? label : undefined}
                        title={PLATFORM_CHIP_MODE === 'icons' ? label : undefined}
                      >
                        {PLATFORM_CHIP_MODE === 'icons' ? <Icon size={29} /> : label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="vault-modal-actions">
                {selectedContent.mediaUrls.length > 1 && (
                  <span className="vault-asset-counter">
                    {lightboxIndex + 1} / {selectedContent.mediaUrls.length}
                  </span>
                )}
                <button
                  className="download-btn"
                  onClick={() => handleDownload(
                    selectedContent,
                    selectedContent.mediaUrls[lightboxIndex],
                    lightboxIndex,
                    selectedContent.mediaUrls.length
                  )}
                >
                  <Download size={16} /> Download
                </button>
                {selectedContent.mediaUrls.length > 1 && selectedContent.zipKey && (
                  <button
                    className="download-btn"
                    onClick={() => handleDownloadZip(selectedContent)}
                  >
                    <Download size={16} /> Download All
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
